import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Pagination, PAGE_SIZE } from "@/components/Pagination";
import {
  Plus,
  UsersThree,
  Phone,
  EnvelopeSimple,
  PencilSimple,
  Rows,
  SquaresFour,
  MapPin,
  Briefcase,
  Check,
  UserCircle,
  Trash,
  ListChecks,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilterBar } from "@/components/FilterBar";
import { AppHeader } from "@/components/AppHeader";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const FILTERS = ["All", "Lead", "Active", "Completed", "Inactive"];
export const CUSTOMER_GROUPS = [
  "Regular",
  "Contractor",
  "Designer",
  "Retail",
  "Builder",
];
const GROUP_FILTERS = ["All", ...CUSTOMER_GROUPS];

const SORTS = [
  ["name", "Name"],
  ["open_jobs", "Open jobs"],
  ["last_activity", "Last touch"],
];
export const CUSTOMER_BADGE = {
  Lead: "bg-yellow-500/10 text-yellow-400 border-yellow-500/25",
  Active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/25",
  Completed: "bg-blue-500/10 text-blue-400 border-blue-500/25",
  Inactive: "bg-zinc-500/10 text-zinc-400 border-zinc-500/25",
};

export const CUSTOMER_DOT = {
  Lead: "bg-yellow-400",
  Active: "bg-emerald-400",
  Completed: "bg-blue-400",
  Inactive: "bg-zinc-400",
};

const initials = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const Avatar = ({ name }) => (
  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-yellow-500/30 bg-yellow-500/10 text-sm font-bold text-yellow-400">
    {initials(name)}
  </span>
);

const Badge = ({ status }) => (
  <span
    className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
      CUSTOMER_BADGE[status] || CUSTOMER_BADGE.Lead
    }`}
  >
    {status}
  </span>
);

// How the account pays: "full" = one invoice, otherwise the default 50/50 split.
const payPlanTag = (plan) =>
  plan === "full"
    ? { label: "Full", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" }
    : { label: "50/50", cls: "border-sky-500/30 bg-sky-500/10 text-sky-300" };

// QuickBooks sync tag — only present when QBO is connected.
const qboTag = (qbo) => {
  if (!qbo) return null;
  if (qbo.status === "synced")
    return { label: "QB ✓", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" };
  if (qbo.status === "failed")
    return { label: "QB ✗", cls: "border-red-500/30 bg-red-500/10 text-red-300" };
  return { label: "QB —", cls: "border-zinc-700 bg-zinc-800 text-zinc-500" };
};

export default function Customers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") || "";
  const viewTitle =
    view === "organizations" ? "Organizations" : view === "people" ? "People" : "All accounts";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState([]);
  const [groupFilter, setGroupFilter] = useState([]);
  const [q, setQ] = useState("");
  const [openBal, setOpenBal] = useState(false);
  const [wonMonth, setWonMonth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("name");
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [qboOn, setQboOn] = useState(false);
  const [qboFilter, setQboFilter] = useState([]);
  const [layout, setLayout] = useState(
    () => localStorage.getItem("bg_accounts_view") || "table",
  );

  useEffect(() => {
    localStorage.setItem("bg_accounts_view", layout);
  }, [layout]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (openBal) params.open_balance = true;
    if (wonMonth) params.won_month = true;
    const [list, st] = await Promise.all([
      api.get("/customers", { params }),
      api.get("/customers/stats"),
    ]);
    setItems(list.data);
    setStats(st.data);
    setLoading(false);
  }, [q, openBal, wonMonth]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api
      .get("/qbo/status")
      .then(({ data }) => setQboOn(!!data.connected))
      .catch(() => setQboOn(false));
  }, []);

  const sorted = [...items].sort((a, b) => {
    if (sort === "open_jobs") return (b.open_jobs || 0) - (a.open_jobs || 0);
    if (sort === "last_activity")
      return (b.last_activity || "").localeCompare(a.last_activity || "");
    return (a.name || "").localeCompare(b.name || "");
  });

  const filtered = sorted
    .filter((c) => {
      const kind = c.contact_type || (c.company ? "organization" : "person");
      return view === "organizations"
        ? kind === "organization"
        : view === "people"
          ? kind === "person"
          : true;
    })
    .filter((c) => filter.length === 0 || filter.includes(c.status))
    .filter((c) => groupFilter.length === 0 || groupFilter.includes(c.group))
    .filter((c) => {
      if (qboFilter.length === 0) return true;
      const st = c.qbo?.status || "pending";
      return qboFilter.some((f) =>
        f === "Not synced"
          ? st !== "synced"
          : f === "Synced"
            ? st === "synced"
            : f === "Failed"
              ? st === "failed"
              : true,
      );
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const visibleIds = filtered.map((c) => c.id);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);
  const bulkDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/customers/bulk-delete", {
        ids: selected,
      });
      toast.success(
        `Deleted ${data.deleted} account${data.deleted === 1 ? "" : "s"}`,
      );
      setSelected([]);
      setConfirmOpen(false);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not delete accounts",
      );
    }
    setDeleting(false);
  };

  const activeCount =
    filter.length +
    groupFilter.length +
    qboFilter.length +
    (sort !== "name" ? 1 : 0);

  const filterBar = (
    <FilterBar
      className="mt-8"
      testidPrefix="accounts"
      right={
        <div className="flex shrink-0 gap-2" data-testid="account-quick-filters">
          <button
            type="button"
            data-testid="customer-filter-open-balance"
            onClick={() => setOpenBal((v) => !v)}
            className={`h-12 rounded-md border px-4 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              openBal
                ? "border-amber-500/50 bg-amber-500/15 text-amber-300"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            Open balance
          </button>
          <button
            type="button"
            data-testid="customer-filter-won-month"
            onClick={() => setWonMonth((v) => !v)}
            className={`h-12 rounded-md border px-4 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
              wonMonth
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"
            }`}
          >
            Won this month
          </button>
        </div>
      }
      search={{
        value: q,
        onChange: setQ,
        placeholder: "Search accounts, company, phone, email…",
        testid: "customer-search-input",
      }}
      sections={[
        {
          key: "status",
          label: "Status",
          multi: true,
          values: filter,
          allValue: "All",
          onChange: setFilter,
          options: FILTERS.map((f) => [
            f,
            f,
            `customer-filter-${f.toLowerCase()}`,
          ]),
        },
        {
          key: "group",
          label: "Group",
          testid: "account-group-filters",
          multi: true,
          values: groupFilter,
          allValue: "All",
          onChange: setGroupFilter,
          options: GROUP_FILTERS.map((g) => [
            g,
            g === "All" ? "All groups" : g,
            `customer-group-filter-${g.toLowerCase()}`,
          ]),
        },
        ...(qboOn
          ? [
              {
                key: "qbo",
                label: "QuickBooks",
                testid: "account-qbo-filters",
                multi: true,
                values: qboFilter,
                allValue: "All",
                onChange: setQboFilter,
                options: ["All", "Not synced", "Failed", "Synced"].map((s) => [
                  s,
                  s,
                  `customer-qbo-filter-${s.toLowerCase().replace(/ /g, "-")}`,
                ]),
              },
            ]
          : []),
        {
          key: "sort",
          label: "Sort by",
          sort: true,
          testid: "accounts-sort",
          chipPrefix: "Sorted by ",
          value: sort,
          allValue: "name",
          onChange: setSort,
          options: SORTS.map(([key, label]) => [
            key,
            label,
            `accounts-sort-${key}`,
          ]),
        },
      ]}
    />
  );

  const editBtn = (c) => (
    <button
      data-testid={`customer-edit-${c.id}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/customers/${c.id}/edit`);
      }}
      title="Edit account"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
    >
      <PencilSimple size={16} weight="bold" />
    </button>
  );

  const openPortal = async (c) => {
    if (!c.email) {
      toast.error("Add an email address to this account first");
      return;
    }
    try {
      const { data } = await api.post(`/customers/${c.id}/portal-link`);
      window.open(
        `${window.location.origin}/portal/enter/${data.token}`,
        "_blank",
        "noopener",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not open the portal",
      );
    }
  };

  const rowActions = (c) => (
    <span className="flex shrink-0 items-center gap-2">
      <button
        data-testid={`customer-portal-${c.id}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openPortal(c);
        }}
        title="Open the customer portal as this account"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-800 text-zinc-400 transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
      >
        <UserCircle size={16} weight="bold" />
      </button>
      {editBtn(c)}
    </span>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={viewTitle} />
      <main className="relative z-10 pb-10 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="accounts-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                CRM
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {viewTitle}
              </h1>
              <p className="mt-1 text-sm text-zinc-500">
                Contacts, job addresses, communication history and scheduled
                jobs in one place
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-4"
            data-testid="accounts-actions"
          >
            <Button
              data-testid="add-customer-button"
              onClick={() => navigate("/customers/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Add Account
            </Button>
            <div className="hidden overflow-hidden rounded-md border border-zinc-800 sm:flex">
              {[
                ["table", Rows],
                ["cards", SquaresFour],
              ].map(([key, Icon]) => (
                <button
                  key={key}
                  data-testid={`accounts-view-${key}`}
                  onClick={() => setLayout(key)}
                  title={`${key} view`}
                  className={`flex h-12 items-center gap-2 px-4 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    layout === key
                      ? "bg-indigo-500 text-white"
                      : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  <Icon size={16} weight="bold" /> {key}
                </button>
              ))}
            </div>
            <Button
              data-testid="multi-edit-toggle"
              variant="outline"
              onClick={() => {
                setSelectMode((v) => !v);
                setSelected([]);
              }}
              className={`h-12 bg-transparent text-xs font-bold uppercase tracking-widest transition-colors duration-150 hover:bg-zinc-800 ${
                selectMode
                  ? "border-indigo-500 text-indigo-300"
                  : "border-zinc-700 text-zinc-300"
              }`}
            >
              <ListChecks size={16} weight="bold" className="mr-2" />
              {selectMode ? "Done" : "Multi edit"}
            </Button>
          </div>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ["Total", stats.total, "text-white", "All"],
              ["Leads", stats.lead, "text-yellow-400", "Lead"],
              ["Active", stats.active, "text-emerald-400", "Active"],
              ["Completed", stats.completed, "text-blue-400", "Completed"],
            ].map(([k, v, c, status]) => {
              const on =
                status === "All"
                  ? filter.length === 0
                  : filter.includes(status);
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  title={
                    status === "All"
                      ? "Show every account"
                      : `Show only ${status.toLowerCase()} accounts`
                  }
                  data-testid={`customer-stat-${k.toLowerCase()}`}
                  onClick={() => setFilter(status === "All" ? [] : [status])}
                  className={`rounded-lg border p-4 text-left transition-colors duration-150 ${
                    on
                      ? "border-indigo-500 bg-indigo-500/10"
                      : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500">
                    {k}
                    {on && (
                      <Check
                        size={11}
                        weight="bold"
                        className="text-indigo-300"
                      />
                    )}
                  </div>
                  <div
                    className={`font-mono-data mt-1 text-2xl font-bold ${c}`}
                  >
                    {v}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {filterBar}

        {loading && (
          <div className="mt-6 space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900"
              />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div
            data-testid="customer-empty-state"
            className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center"
          >
            <UsersThree
              size={28}
              weight="bold"
              className="mx-auto text-zinc-700"
            />
            <p className="mt-3 text-sm text-zinc-400">
              No accounts yet. Add your first one.
            </p>
          </div>
        )}

        {selectMode && (
          <div
            data-testid="bulk-action-bar"
            className="mt-4 flex items-center gap-3 rounded-lg border border-indigo-500/40 bg-indigo-500/10 px-4 py-3"
          >
            <span
              className="text-sm font-semibold text-indigo-200"
              data-testid="bulk-selected-count"
            >
              {selected.length} selected
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                data-testid="bulk-clear"
                onClick={() => setSelected([])}
                className="text-xs uppercase tracking-widest text-zinc-400 transition-colors duration-150 hover:text-zinc-200"
              >
                Clear
              </button>
            )}
            <Button
              data-testid="bulk-delete-button"
              disabled={selected.length === 0}
              onClick={() => setConfirmOpen(true)}
              className="ml-auto h-10 bg-red-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-red-500 disabled:opacity-40"
            >
              <Trash size={15} weight="bold" className="mr-2" /> Delete selected
            </Button>
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="bulk-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {selected.length} account
                {selected.length === 1 ? "" : "s"}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the selected accounts and their
                activity history. Linked jobs, quotes and invoices are kept but
                lose their account link. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="bulk-delete-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="bulk-delete-confirm"
                onClick={bulkDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-500"
              >
                {deleting ? "Deleting…" : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* table view (desktop) */}
        {!loading && items.length > 0 && layout === "table" && (
          <div
            className="mt-6 hidden overflow-hidden rounded-lg border border-zinc-800 lg:block"
            data-testid="accounts-table"
          >
            <div className={`grid ${selectMode ? "grid-cols-[auto_2fr_1.2fr_1.5fr_0.9fr_0.8fr_0.9fr_auto]" : "grid-cols-[2fr_1.2fr_1.5fr_0.9fr_0.8fr_0.9fr_auto]"} gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-3 text-[11px] uppercase tracking-widest text-zinc-500`}>
              {selectMode && (
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="bulk-select-all"
                  aria-label="Select all accounts"
                  className="h-4 w-4 self-center accent-indigo-500"
                />
              )}
              <span>Account</span>
              <span>Company</span>
              <span>Reach</span>
              <span>Job sites</span>
              <span>Open jobs</span>
              <span>Status</span>
              <span />
            </div>
            {paged.map((c) => (
              <div
                key={c.id}
                className={`grid ${selectMode ? "grid-cols-[auto_2fr_1.2fr_1.5fr_0.9fr_0.8fr_0.9fr_auto]" : "grid-cols-[2fr_1.2fr_1.5fr_0.9fr_0.8fr_0.9fr_auto]"} items-center gap-4 border-b border-zinc-800/70 bg-zinc-900 px-4 py-3 transition-colors duration-150 last:border-0 hover:bg-zinc-800/40`}
              >
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={selected.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    data-testid={`customer-select-${c.id}`}
                    aria-label={`Select ${c.name}`}
                    className="h-4 w-4 accent-indigo-500"
                  />
                )}
                <Link
                  to={`/customers/${c.id}`}
                  data-testid={`customer-card-${c.id}`}
                  className="flex min-w-0 items-center gap-3"
                >
                  <Avatar name={c.name} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold">
                      {c.name}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {c.group && c.group !== "Regular" && (
                        <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                          {c.group}
                        </span>
                      )}
                      <span
                        data-testid={`customer-pay-plan-${c.id}`}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${payPlanTag(c.pay_plan).cls}`}
                      >
                        {payPlanTag(c.pay_plan).label}
                      </span>
                      {qboTag(c.qbo) && (
                        <span
                          data-testid={`customer-qbo-${c.id}`}
                          title="QuickBooks sync"
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${qboTag(c.qbo).cls}`}
                        >
                          {qboTag(c.qbo).label}
                        </span>
                      )}
                    </span>
                  </span>
                </Link>
                <div
                  className="min-w-0 text-xs text-zinc-300"
                  data-testid={`customer-company-${c.id}`}
                >
                  {c.company ? (
                    <span className="flex items-center gap-1.5">
                      <Briefcase
                        size={12}
                        weight="bold"
                        className="shrink-0 text-zinc-500"
                      />
                      <span className="truncate">{c.company}</span>
                    </span>
                  ) : (
                    <span className="text-zinc-600">—</span>
                  )}
                </div>
                <div className="min-w-0 text-xs text-zinc-400">
                  {c.phone && (
                    <span className="flex items-center gap-1 truncate">
                      <Phone size={12} weight="bold" /> {c.phone}
                    </span>
                  )}
                  {c.email && (
                    <span className="flex items-center gap-1 truncate">
                      <EnvelopeSimple size={12} weight="bold" /> {c.email}
                    </span>
                  )}
                  {!c.phone && !c.email && "—"}
                </div>
                <div className="font-mono-data text-sm text-zinc-300">
                  {(c.job_addresses || []).length || "—"}
                  {(c.contacts || []).length > 0 && (
                    <span className="ml-2 text-[11px] text-zinc-500">
                      {c.contacts.length} contact
                      {c.contacts.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <div className="font-mono-data text-sm">
                  <span
                    className={
                      c.open_jobs ? "text-yellow-400" : "text-zinc-500"
                    }
                  >
                    {c.open_jobs || 0}
                  </span>
                  {c.next_stop && (
                    <span className="ml-2 text-[11px] text-zinc-500">
                      next {c.next_stop}
                    </span>
                  )}
                  {c.open_balance > 0 && (
                    <span className="ml-2 text-[11px] font-bold text-amber-300">
                      ${Math.round(c.open_balance).toLocaleString()} due
                    </span>
                  )}
                  {c.won_this_month > 0 && (
                    <span className="ml-2 text-[11px] font-bold text-emerald-300">
                      Won
                    </span>
                  )}
                </div>
                <Badge status={c.status} />
                {rowActions(c)}
              </div>
            ))}
          </div>
        )}

        {/* card grid (and all mobile) */}
        {!loading && items.length > 0 && (
          <div
            className={`mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-3 ${
              layout === "table" ? "lg:hidden" : ""
            }`}
            data-testid="accounts-cards"
          >
            {paged.map((c) => (
              <div
                key={c.id}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4 transition-colors duration-150 hover:border-zinc-700"
              >
                <div className="flex items-start gap-3">
                  {selectMode && (
                    <input
                      type="checkbox"
                      checked={selected.includes(c.id)}
                      onChange={() => toggle(c.id)}
                      data-testid={`customer-select-card-${c.id}`}
                      aria-label={`Select ${c.name}`}
                      className="mt-1 h-4 w-4 shrink-0 accent-indigo-500"
                    />
                  )}
                  <Avatar name={c.company || c.name} />
                  <Link
                    to={`/customers/${c.id}`}
                    data-testid={`customer-card-${c.id}`}
                    className="min-w-0 flex-1"
                  >
                    <h3 className="truncate text-base font-bold">
                      {c.company || c.name}
                    </h3>
                    {c.company && (
                      <p className="truncate text-xs text-zinc-400">{c.name}</p>
                    )}
                    <span className="mt-1 flex flex-wrap items-center gap-1.5">
                      {c.group && c.group !== "Regular" && (
                        <span className="rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                          {c.group}
                        </span>
                      )}
                      <span
                        data-testid={`customer-pay-plan-card-${c.id}`}
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${payPlanTag(c.pay_plan).cls}`}
                      >
                        {payPlanTag(c.pay_plan).label}
                      </span>
                      {qboTag(c.qbo) && (
                        <span
                          data-testid={`customer-qbo-card-${c.id}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${qboTag(c.qbo).cls}`}
                        >
                          {qboTag(c.qbo).label}
                        </span>
                      )}
                    </span>
                  </Link>
                  <Badge status={c.status} />
                </div>

                <div className="mt-3 space-y-1 text-xs text-zinc-400">
                  {c.phone && (
                    <a
                      href={`tel:${c.phone}`}
                      className="flex items-center gap-2 hover:text-yellow-400"
                    >
                      <Phone size={13} weight="bold" /> {c.phone}
                    </a>
                  )}
                  {c.email && (
                    <a
                      href={`mailto:${c.email}`}
                      className="flex items-center gap-2 truncate hover:text-yellow-400"
                    >
                      <EnvelopeSimple size={13} weight="bold" /> {c.email}
                    </a>
                  )}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-3 text-[11px] uppercase tracking-widest text-zinc-500">
                  <span className="inline-flex items-center gap-1">
                    <Briefcase size={12} weight="bold" />
                    <span className="font-mono-data text-zinc-300">
                      {c.open_jobs || 0}
                    </span>{" "}
                    open
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} weight="bold" />
                    <span className="font-mono-data text-zinc-300">
                      {(c.job_addresses || []).length}
                    </span>{" "}
                    sites
                  </span>
                  {c.last_activity && <span>last touch {c.last_activity}</span>}
                  <span className="ml-auto">{rowActions(c)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <Pagination
          page={curPage}
          total={filtered.length}
          onPage={setPage}
          testId="accounts-pagination"
        />
      </main>
    </div>
  );
}
